import { useEffect, useState, type MouseEvent } from 'react';
import {
  assistantRuleRevoke,
  assistantRuleRuns,
  assistantRuleSave,
  assistantRules,
  assistantRulesRevokeAll,
  assistantTaskCancel,
  assistantTasks,
  assistantTeamUsage,
  assistantUsage,
  assistantWatchLevel,
  assistantWatchStop,
  assistantWatches,
  atlasContext,
  atlasMemoryForget,
  travelPolicy,
  travelPolicySave,
  type ApiError,
  type AssistantRule,
  type AssistantRuleMode,
  type TravelCabin,
  type TravelPolicy,
  type WatchLevel,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, notServed, pre, sx, useApi, useGuarded } from './kit';
import { passkeyStepUp } from './stepUp';
import { BuildLabel, BuildTagged } from '../../ui/buildLabels';
import { AboutThis } from '../../ui/AboutThis';

// Settings, Atlas Assistant: the three tabs after How Atlas behaves (Console V5; design
// T10380-10600, values V5 L37771-37880). Everything is the signed-in person's own, from the
// /assistant/* routes (v3/settings/assistant-rules.ts; the Aithora team only, the server
// refuses everyone else). Where a route is not served yet the design's own label says so
// (SAMPLE · LIST NOT BUILT YET) and its rows are the design's samples; nothing else here is
// made up. The Chrome copilot tab is the design only: DESIGN · NOT BUILT YET (AF-1582).

const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const ROWLINE = 'border-top:1px solid rgba(34,39,46,.08);';
const RED_LINK = 'flex:0 0 auto;font-size:16px;font-weight:700;color:#B3261E;text-decoration:none;';
const TAG = 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.08);white-space:nowrap;';
const pl = (on: boolean) => 'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (on ? 'background:#264C7E;color:#fff;' : 'border:1px solid rgba(34,39,46,.16);color:#3C454F;');
const BTN30 = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;';
const hm = (iso: string | number) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const DAY = 86_400_000;
const toErr = (e: unknown): ApiError => (e instanceof Error ? (e as ApiError) : ({ message: String(e) } as ApiError));

// ── Rules and schedules ─────────────────────────────────────────────────────

/** The kinds a person may add here, each with the design's fixed condition (COND, V5 L37786). */
const KINDS: { label: string; tool: string; cond: string; conditions?: Record<string, unknown> }[] = [
  { label: 'Send emails', tool: 'gmail_send', cond: 'only to @aithoracare.com', conditions: { to_domains: ['aithoracare.com'] } },
  { label: 'Calendar events', tool: 'calendar_create_event', cond: 'up to 60 minutes, invitees at @aithoracare.com', conditions: { max_minutes: 60, attendee_domains: ['aithoracare.com'] } },
  { label: 'Linear tickets', tool: 'linear_create_issue', cond: 'in the Console project' },
  { label: 'CRM prospects', tool: 'create_prospect', cond: 'create and update, never delete' },
  { label: 'Memory notes', tool: 'remember', cond: 'my own notes only' },
  { label: 'Scheduled tasks', tool: 'schedule_task', cond: 'results to my own phone only' },
];
const MODES: [string, AssistantRuleMode][] = [['Allow', 'allow'], ['Ask', 'ask'], ['Never', 'block']];
const DURS: [string, number | undefined][] = [['24 hours', 24], ['30 days', 720], ['Until I revoke', undefined]];
const MADE: Record<string, string> = { settings: 'Added here', text: 'By text', approval: 'From an approval card', assistant: 'In chat' };

function ruleMeta(r: AssistantRule): { tag: 'ON' | 'PRACTICE'; meta: string } {
  const practice = !!r.practiceUntil && Date.parse(r.practiceUntil) > Date.now();
  const made = (MADE[r.createdFrom ?? ''] ?? 'Added') + ' · ' + new Date(r.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' });
  let until = 'until you revoke it';
  if (practice) until = 'practice week, day ' + Math.min(7, Math.max(1, 7 - Math.floor((Date.parse(r.practiceUntil as string) - Date.now()) / DAY))) + ' of 7';
  else if (r.expiresAt) {
    const left = Math.ceil((Date.parse(r.expiresAt) - Date.now()) / DAY);
    until = left <= 1 ? 'ends ' + hm(r.expiresAt) : left + ' days left';
  }
  const track = r.runCount === undefined ? '' : ' · ' + r.runCount + ' ran · ' + (r.undoCount ?? 0) + ' undone';
  return { tag: practice ? 'PRACTICE' : 'ON', meta: until + ' · ' + made + track };
}

/** The design's sample schedules, shown only (with its label) while GET /assistant/tasks is not served. */
const SAMPLE_SCHED: [string, string, string][] = [
  ['Text me my day', 'Every weekday 7:00 AM · next: Mon 7:00 AM', 'Last: today 7:00 AM, sent by text'],
  ['Census, falls and open items brief', 'Mondays 8:00 AM · next: Mon Oct 5', 'Last: Sep 28, in the app'],
  ['Remind me: Gracy Woods BAA renewal', 'Once · Oct 20, 9:00 AM', 'Not run yet'],
];
const SAMPLE_WATCH: [string, string][] = [
  ['Email from Gracy Woods, Belterra and Parmer administrators', 'More'],
  ['Calendar clashes and meetings starting in 10 minutes', 'Normal'],
  ['New senior-care events and registration deadlines', 'Less'],
  ['Facility score changes over 5 points', 'Less'],
];
const LEVELS: [string, WatchLevel][] = [['More', 'more'], ['Normal', 'normal'], ['Less', 'less'], ['Off', 'off']];
const LEVEL_HOW: Record<WatchLevel, string> = { more: 'urgent · text now', normal: 'text now', less: '7:30 AM brief', off: 'paused · nothing sent' };

export function AssistantRulesTab() {
  const { client } = useSession();
  const { wr } = useGuarded();
  const rules = useApi((c, s) => assistantRules(c, { signal: s }), []);
  const runs = useApi((c, s) => assistantRuleRuns(c, { signal: s }), []);
  const tasks = useApi((c, s) => assistantTasks(c, { signal: s }), []);
  const watches = useApi((c, s) => assistantWatches(c, { signal: s }), []);
  const ctx = useApi((c, s) => atlasContext(c, { signal: s }), []);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState(0);
  const [mode, setMode] = useState(0);
  const [dur, setDur] = useState(1);
  const [err, setErr] = useState<ApiError | null>(null);

  if (!rules.data) return rules.error ? <ErrLine error={rules.error} /> : <Skeleton rows={5} label="Loading your rules" />;
  const list = rules.data.rules;
  const k = KINDS[kind];
  const [mName, mKey] = MODES[mode];
  const draftHead = mKey === 'allow' ? 'Always allow ' : mKey === 'ask' ? 'Always ask before ' : 'Never ';
  const draft = draftHead + k.label.toLowerCase() + ' ' + k.cond + ', for ' + DURS[dur][0].toLowerCase() + '.';
  const save = async () => {
    setErr(null);
    try {
      // The rule is the person's own click: a fresh passkey first, as the card says.
      await passkeyStepUp(client);
      await assistantRuleSave(client, { body: { tool: k.tool, mode: mKey, ...(k.conditions ? { conditions: k.conditions } : {}), ...(DURS[dur][1] ? { hours: DURS[dur][1] } : {}) } });
      setAdding(false);
      rules.reload();
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const revoke = async (id: string) => {
    setErr(null);
    try {
      await assistantRuleRevoke(client, { params: { ruleId: id } });
      rules.setData((p) => (p ? { ...p, rules: p.rules.filter((x) => x.id !== id) } : p));
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const ran = (runs.data?.runs ?? []).filter((x) => Date.now() - Date.parse(x.at) < DAY);
  const tasksSample = notServed(tasks.error);
  const watchSample = notServed(watches.error);
  const memory = (ctx.data?.memory ?? []).filter((m) => !m.forgotten);
  const setLevel = async (id: string, level: WatchLevel) => {
    setErr(null);
    try {
      const r = await assistantWatchLevel(client, { params: { id }, body: { level } });
      watches.setData((p) => (p ? { ...p, watches: p.watches.map((w) => (w.id === id ? { ...w, level: r.watch.level } : w)) } : p));
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const stopWatch = async (id: string) => {
    setErr(null);
    try {
      await assistantWatchStop(client, { params: { id } });
      watches.setData((p) => (p ? { ...p, watches: p.watches.filter((w) => w.id !== id) } : p));
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const cancelTask = async (id: string) => {
    setErr(null);
    try {
      await assistantTaskCancel(client, { params: { id } });
      tasks.setData((p) => (p ? { ...p, tasks: p.tasks.filter((t) => t.id !== id) } : p));
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const forget = async (id: string) => {
    setErr(null);
    try {
      await atlasMemoryForget(client, { params: { id } });
      ctx.setData((p) => (p ? { ...p, memory: p.memory.map((m) => (m.id === id ? { ...m, forgotten: true } : m)) } : p));
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const levelRow = (cur: WatchLevel, on: (l: WatchLevel) => void, stop: () => void) => (
    <div style={sx('display:flex;align-items:center;gap:5px;flex-wrap:wrap;')}>
      {LEVELS.map(([n, l]) => (
        <a key={l} href="#" {...wr(() => on(l))} style={sx(pl(cur === l))}>{n}</a>
      ))}
      <span style={sx('flex:1 1 10px;')} />
      <a href="#" {...wr(() => stop())} style={sx('font-size:16px;font-weight:700;color:#B3261E;text-decoration:none;')}>Stop watching</a>
    </div>
  );

  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px 28px;min-width:0;')}>
      <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
          <span style={sx(CAPT)}>My rules</span>
          <span style={sx('font-size:16px;color:#54606C;')}>{list.length + (list.length === 1 ? ' rule' : ' rules') + ' · yours only · team only'}</span>
          <span style={sx('flex:1 1 10px;')} />
          <a href="#" onClick={(e) => { pre(e); setAdding(!adding); }} style={sx(BTN30 + 'border:1px solid rgba(47,92,151,.4);color:#2F5C97;height:28px;')}>Add a rule</a>
        </div>
        {adding && (
          <div style={sx('display:flex;flex-direction:column;gap:7px;margin-top:8px;padding:10px 12px;border:1px solid rgba(47,92,151,.3);border-radius:10px;background:#FCFAF4;')}>
            <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
              {KINDS.map((x, i) => <a key={x.tool} href="#" onClick={(e) => { pre(e); setKind(i); }} style={sx(pl(kind === i))}>{x.label}</a>)}
            </div>
            <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
              {MODES.map(([n], i) => <a key={n} href="#" onClick={(e) => { pre(e); setMode(i); }} style={sx(pl(mode === i))}>{n}</a>)}
              <span style={sx('width:10px;')} />
              {DURS.map(([n], i) => <a key={n} href="#" onClick={(e) => { pre(e); setDur(i); }} style={sx(pl(dur === i))}>{n}</a>)}
            </div>
            <span style={sx('font-size:16px;color:#22272E;line-height:1.45;')}>{draft}</span>
            <AboutThis inline textStyle="font-size:16px;color:#54606C;">
              New rules run one week in practice first: shown as “would have done”, not done. Money, care, paging and the kill switch can never have a rule.
            </AboutThis>
            <div style={sx('display:flex;gap:6px;')}>
              <a href="#" {...wr(() => void save())} aria-label={'Save rule: ' + mName} style={sx(BTN30 + 'background:#264C7E;color:#fff;')}>Save rule · passkey</a>
              <a href="#" onClick={(e) => { pre(e); setAdding(false); }} style={sx(BTN30 + 'color:#54606C;font-weight:600;')}>Cancel</a>
            </div>
          </div>
        )}
        {list.map((r) => {
          const m = ruleMeta(r);
          return (
            <div key={r.id} style={sx('display:flex;align-items:flex-start;gap:10px;padding:8px 0;min-width:0;' + ROWLINE)}>
              <BuildTagged tag={m.tag}><span style={sx('flex:0 0 auto;display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;margin-top:1px;' + (m.tag === 'PRACTICE' ? 'color:#5B3A8C;background:rgba(122,106,174,.16);' : 'color:#12503C;background:rgba(31,107,82,.13);'))}>{m.tag}</span></BuildTagged>
              <div style={sx('flex:1 1 auto;min-width:0;')}>
                <div style={sx('font-size:16px;font-weight:600;color:#22272E;line-height:1.4;text-wrap:pretty;')}>{r.sentence}</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:2px;')}>{m.meta}</div>
              </div>
              <a href="#" {...wr(() => void revoke(r.id))} style={sx(RED_LINK + 'padding-top:1px;')}>Revoke</a>
            </div>
          );
        })}
        {list.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:8px 0;' + ROWLINE)}>Atlas asks before every action. Add a rule to let it handle routine ones.</div>}
        <div style={sx(CAPT + 'margin:12px 0 2px;')}>What rules ran · last 24 hours</div>
        {ran.map((x, i) => (
          <div key={i} style={sx('display:flex;gap:8px;padding:5px 0;font-size:16px;min-width:0;' + ROWLINE)}>
            <span style={sx('flex:0 0 auto;color:#54606C;font-variant-numeric:tabular-nums;')}>{hm(x.at)}</span>
            <span style={sx('flex:1 1 auto;min-width:0;color:#22272E;')}>{x.what}</span>
            <span style={sx('flex:0 0 auto;color:#54606C;')}>{x.outcome ?? x.status}</span>
          </div>
        ))}
        {ran.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:5px 0;' + ROWLINE)}>{runs.error ? runs.error.message : 'No rule ran in the last 24 hours.'}</div>}
        <ErrLine error={err} />
      </div>
      <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
          <span style={sx(CAPT)}>Scheduled</span>
          <span style={sx('font-size:16px;color:#54606C;')}>results go only to your own phone or the app</span>
          <span style={sx('flex:1 1 10px;')} />
          {tasksSample && <BuildLabel><span style={sx(TAG)}>SAMPLE · LIST NOT BUILT YET</span></BuildLabel>}
        </div>
        {tasksSample
          ? SAMPLE_SCHED.map((x) => (
            <div key={x[0]} style={sx('display:flex;align-items:flex-start;gap:10px;padding:8px 0;min-width:0;' + ROWLINE)}>
              <div style={sx('flex:1 1 auto;min-width:0;')}>
                <div style={sx('font-size:16px;font-weight:600;color:#22272E;')}>{x[0]}</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:2px;')}>{x[1]}</div>
                <div style={sx('font-size:16px;color:#3C454F;margin-top:1px;')}>{x[2]}</div>
              </div>
              <a href="#" onClick={pre} aria-disabled="true" title="Sample: the list is not built yet" style={sx(RED_LINK)}>Cancel</a>
            </div>
          ))
          : (tasks.data?.tasks ?? []).map((t) => (
            <div key={t.id} style={sx('display:flex;align-items:flex-start;gap:10px;padding:8px 0;min-width:0;' + ROWLINE)}>
              <div style={sx('flex:1 1 auto;min-width:0;')}>
                <div style={sx('font-size:16px;font-weight:600;color:#22272E;')}>{t.title}</div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:2px;')}>{t.when + (t.nextRun ? ' · next: ' + new Date(t.nextRun).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '')}</div>
                <div style={sx('font-size:16px;color:#3C454F;margin-top:1px;')}>{t.lastRun ? 'Last: ' + new Date(t.lastRun).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + (t.lastStatus ? ', ' + t.lastStatus : '') : 'Not run yet'}</div>
              </div>
              <a href="#" {...wr(() => void cancelTask(t.id))} style={sx(RED_LINK)}>Cancel</a>
            </div>
          ))}
        <div style={sx('font-size:16px;color:#54606C;padding:6px 0;line-height:1.45;' + ROWLINE)}>Ask Atlas to add one: “every weekday at 7, text me my day”. It needs your YES. Schedules never act on their own and stop while the AI is paused.</div>
        <div style={sx('display:flex;align-items:baseline;gap:8px;margin:12px 0 2px;')}>
          <span style={sx(CAPT)}>Watches · heads-ups</span>
          {watchSample && <><span style={sx('flex:1 1 10px;')} /><BuildLabel><span style={sx(TAG)}>SAMPLE · NOT BUILT YET</span></BuildLabel></>}
        </div>
        {watchSample
          ? SAMPLE_WATCH.map((w) => (
            <div key={w[0]} style={sx('display:flex;flex-direction:column;gap:5px;padding:7px 0;min-width:0;' + ROWLINE)}>
              <div style={sx('display:flex;gap:8px;font-size:16px;min-width:0;')}>
                <span style={sx('flex:1 1 auto;min-width:0;color:#22272E;font-weight:600;line-height:1.4;')}>{w[0]}</span>
                <span style={sx('flex:0 0 auto;color:#54606C;font-size:16px;')}>{LEVEL_HOW[w[1].toLowerCase() as WatchLevel]}</span>
              </div>
              {levelRow(w[1].toLowerCase() as WatchLevel, () => undefined, () => undefined)}
            </div>
          ))
          : (watches.data?.watches ?? []).map((w) => (
            <div key={w.id} style={sx('display:flex;flex-direction:column;gap:5px;padding:7px 0;min-width:0;' + ROWLINE)}>
              <div style={sx('display:flex;gap:8px;font-size:16px;min-width:0;')}>
                <span style={sx('flex:1 1 auto;min-width:0;color:#22272E;font-weight:600;line-height:1.4;')}>{w.title}</span>
                <span style={sx('flex:0 0 auto;color:#54606C;font-size:16px;')}>{LEVEL_HOW[w.level] ?? w.level}</span>
              </div>
              {levelRow(w.level, (l) => void setLevel(w.id, l), () => void stopWatch(w.id))}
            </div>
          ))}
        {!watchSample && watches.data && watches.data.watches.length === 0 && (
          <div style={sx('font-size:16px;color:#54606C;padding:7px 0;' + ROWLINE)}>Atlas isn’t watching anything for you. Ask it: “tell me when Belterra replies”.</div>
        )}
        <div style={sx('font-size:16px;color:#54606C;padding:6px 0;' + ROWLINE)}>Urgent texts now; the rest in one 7:30 AM brief. Quiet 9 PM to 7 AM. Up to 6 texts a day.</div>
        <div style={sx(CAPT + 'margin:12px 0 2px;')}>What Atlas remembers about you</div>
        {memory.map((m) => (
          <div key={m.id} style={sx('display:flex;gap:10px;padding:6px 0;font-size:16px;min-width:0;' + ROWLINE)}>
            <div style={sx('flex:1 1 auto;min-width:0;')}>
              <div style={sx('color:#22272E;line-height:1.4;text-wrap:pretty;')}>{m.key + ': ' + m.value}</div>
              <div style={sx('font-size:16px;color:#54606C;margin-top:1px;')}>{m.expiresAt ? 'Kept until ' + new Date(m.expiresAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) : 'Kept until you forget it'}</div>
            </div>
            <a href="#" {...wr(() => void forget(m.id))} style={sx(RED_LINK)}>Forget</a>
          </div>
        ))}
        {ctx.data && memory.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;' + ROWLINE)}>Nothing yet.</div>}
        <div style={sx('font-size:16px;color:#54606C;padding:6px 0;line-height:1.45;' + ROWLINE)}>Yours only. Never resident health details. Tap “Remember this” under any answer to add one.</div>
      </div>
    </div>
  );
}

// ── Use and spend ───────────────────────────────────────────────────────────

const CABIN: Record<TravelCabin, string> = { economy: 'Economy', premium_economy: 'Premium economy', business: 'Business', first: 'First' };
const usd = (n: number | null | undefined) => (n === null || n === undefined ? 'No cap' : '$' + Math.round(n).toLocaleString());
const listOf = (xs: string[]) => (xs.length <= 1 ? xs.join('') : xs.slice(0, -1).join(', ') + ', ' + xs[xs.length - 1].toLowerCase());
const POLICY_ROWS: { key: string; label: string; show: (p: TravelPolicy) => string }[] = [
  { key: 'cab', label: 'Cabins allowed', show: (p) => listOf(p.allowedCabins.map((c) => CABIN[c] ?? c)) },
  { key: 'fare', label: 'Fare cap, per person per trip', show: (p) => usd(p.maxFarePerPersonUsd) },
  { key: 'hotN', label: 'Hotel cap, per night', show: (p) => usd(p.hotelMaxNightUsd) },
  { key: 'hotW', label: 'Hotel cap, per week', show: (p) => usd(p.hotelMaxWeekUsd) },
  { key: 'air', label: 'Preferred airlines', show: (p) => (p.preferredAirlines.length ? p.preferredAirlines.join(', ') : 'Any') },
  { key: 'who', label: 'Who may travel', show: (p) => (p.travellerUserIds.length ? p.travellerUserIds.length + (p.travellerUserIds.length === 1 ? ' person' : ' people') : 'The Aithora team') },
  { key: 'ok', label: 'Needs Andrew’s OK', show: (p) => 'Over ' + usd(p.ownerOkAboveUsd) + ', or 7-day total over ' + usd(p.ownerOkAboveUsd) },
];
const num = (s: string): number | null => {
  const n = Number(String(s).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && String(s).trim() !== '' ? n : null;
};
const cabinsOf = (s: string): TravelCabin[] =>
  s.split(/,|\band\b/).map((x) => x.trim().toLowerCase().replace(/\s+/g, '_')).filter((x): x is TravelCabin => x in CABIN);

export function AssistantUseTab({ owner }: { owner: boolean }) {
  const { client, session } = useSession();
  const { wr } = useGuarded();
  const mine = useApi((c, s) => assistantUsage(c, { signal: s }), []);
  const team = useApi((c, s) => assistantTeamUsage(c, { signal: s }), [], owner);
  const pol = useApi((c, s) => travelPolicy(c, { signal: s }), []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [why, setWhy] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const [revoked, setRevoked] = useState<number | null>(null);

  const meName = (session?.user.name ?? 'You').split(/\s+/)[0];
  const people = owner && team.data
    ? team.data.people.map((p) => ({ who: p.userId === session?.user.id ? (p.name || meName) + ' (you)' : p.name || p.email, cost: p.costUsd, cap: p.capUsd, pace: p.hitsCapLocal }))
    : mine.data ? [{ who: meName + ' (you)', cost: mine.data.costUsd, cap: mine.data.capUsd, pace: null as string | null }] : [];
  const p = pol.data?.policy ?? null;
  const canEdit = !!pol.data?.canEdit && owner;
  const save = async () => {
    if (!p || !why.trim()) return;
    setErr(null);
    try {
      const body: Partial<TravelPolicy> = {};
      if (draft.cab !== undefined) body.allowedCabins = cabinsOf(draft.cab);
      if (draft.fare !== undefined) { const n = num(draft.fare); if (n !== null) body.maxFarePerPersonUsd = n; }
      if (draft.hotN !== undefined) body.hotelMaxNightUsd = num(draft.hotN);
      if (draft.hotW !== undefined) { const n = num(draft.hotW); if (n !== null) body.hotelMaxWeekUsd = n; }
      if (draft.air !== undefined) body.preferredAirlines = draft.air.split(',').map((x) => x.trim()).filter(Boolean);
      if (draft.ok !== undefined) { const n = num((draft.ok.match(/\$?[\d,]+/) ?? [''])[0]); if (n !== null) body.ownerOkAboveUsd = n; }
      const stepUpToken = await passkeyStepUp(client);
      const r = await travelPolicySave(client, { body: { ...body, reason: why.trim(), stepUpToken } });
      pol.setData((x) => (x ? { ...x, policy: r.policy } : x));
      setEditing(false);
      setDraft({});
      setWhy('');
    } catch (e) {
      setErr(toErr(e));
    }
  };
  const revokeAll = async () => {
    setErr(null);
    try {
      const r = await assistantRulesRevokeAll(client);
      setRevoked(Date.now());
      void r;
    } catch (e) {
      setErr(toErr(e));
    }
  };

  return (
    <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px 28px;min-width:0;')}>
      <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
          <span style={sx(CAPT)}>AI use today</span>
          <span style={sx('font-size:16px;color:#54606C;')}>{owner ? 'everyone · resets midnight Central' : 'yours · resets midnight Central'}</span>
        </div>
        {!mine.data && !mine.error && <Skeleton rows={2} label="Loading your AI use" />}
        {people.map((x) => {
          const pct = x.cap > 0 ? Math.min(100, Math.round((100 * x.cost) / x.cap)) : 0;
          return (
            <div key={x.who} style={sx('display:flex;flex-direction:column;gap:4px;padding:8px 0;min-width:0;' + ROWLINE)}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;font-size:16px;')}>
                <span style={sx('font-weight:700;color:#22272E;')}>{x.who}</span>
                <span style={sx('flex:1 1 10px;')} />
                <span style={sx('font-variant-numeric:tabular-nums;color:#22272E;font-weight:600;')}>{'$' + x.cost.toFixed(2) + ' of $' + x.cap}</span>
              </div>
              <div style={sx('height:5px;border-radius:3px;background:rgba(34,39,46,.08);overflow:hidden;')}>
                <div style={sx('height:100%;width:' + pct + '%;background:' + (pct >= 80 ? '#B3261E' : pct >= 50 ? '#C2702F' : '#1F6B52') + ';')} />
              </div>
              <div style={sx('font-size:16px;color:' + (x.pace ? '#8A1C16' : '#54606C') + ';')}>{x.pace ? 'At today’s pace: hits limit around ' + x.pace : pct >= 80 ? 'Heads-up text sent at 80%' : 'Not on pace to hit the limit today'}</div>
            </div>
          );
        })}
        {mine.error && <ErrLine error={mine.error} />}
        <div style={sx('font-size:16px;color:#54606C;padding:6px 0;line-height:1.45;' + ROWLINE)}>
          {owner ? 'One heads-up text at 80%. At the cap Atlas says so until midnight. Atlas and crisis paths are never capped. Ask Atlas “who is using AI today?” for the same list.' : 'One heads-up text at 80%. Only Andrew sees everyone and changes caps.'}
        </div>
        {owner && (
          <>
            <div style={sx('display:flex;align-items:baseline;gap:8px;margin:12px 0 2px;')}>
              <span style={sx(CAPT)}>Learning toward our own model</span>
              <span style={sx('flex:1 1 10px;')} />
              <span style={sx(TAG)}>NOT BUILT YET</span>
            </div>
            {['Your yes, no and edits saved for training this month', 'Jobs our own model has passed (of 6)', 'Shadow days logged (of 14)', 'Corrections that changed a draft'].map((k) => (
              <div key={k} style={sx('display:flex;gap:8px;padding:5px 0;font-size:16px;' + ROWLINE)}>
                <span style={sx('flex:1 1 auto;color:#22272E;')}>{k}</span>
                <span style={sx('font-weight:700;color:#54606C;font-variant-numeric:tabular-nums;')}>–</span>
              </div>
            ))}
          </>
        )}
      </div>
      <div style={sx('display:flex;flex-direction:column;min-width:0;')}>
        <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
          <span style={sx(CAPT)}>Travel and spend policy</span>
          <span style={sx(canEdit ? 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;color:#264C7E;background:rgba(47,92,151,.12);' : TAG)}>{canEdit ? 'OWNER ONLY' : 'READ ONLY'}</span>
        </div>
        {!p && pol.error && <div style={sx('font-size:16px;color:#54606C;padding:6px 0;' + ROWLINE)}>{pol.error.message}</div>}
        {p && !editing && POLICY_ROWS.map((r) => (
          <div key={r.key} style={sx('display:flex;gap:10px;padding:6px 0;font-size:16px;min-width:0;' + ROWLINE)}>
            <span style={sx('flex:1 1 auto;min-width:0;color:#3C454F;')}>{r.label}</span>
            <span style={sx('flex:0 0 auto;font-weight:700;color:#22272E;text-align:right;')}>{r.show(p)}</span>
          </div>
        ))}
        {p && !editing && (
          <div style={sx('display:flex;gap:10px;padding:6px 0;font-size:16px;min-width:0;' + ROWLINE)}>
            <span style={sx('flex:1 1 auto;min-width:0;color:#3C454F;')}>Never</span>
            <span style={sx('flex:0 0 auto;font-weight:700;color:#22272E;text-align:right;')}>Card numbers typed in chat</span>
          </div>
        )}
        {p && editing && (
          <>
            {POLICY_ROWS.filter((r) => r.key !== 'who').map((r) => (
              <label key={r.key} style={sx('display:flex;align-items:center;gap:10px;padding:5px 0;font-size:16px;min-width:0;' + ROWLINE)}>
                <span style={sx('flex:1 1 140px;min-width:0;color:#3C454F;')}>{r.label}</span>
                <input aria-label={r.label} value={draft[r.key] ?? r.show(p)} onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })} style={sx("flex:1 1 170px;min-width:0;height:30px;box-sizing:border-box;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FCFAF4;font-family:'Archivo',sans-serif;font-size:16px;font-weight:600;color:#22272E;")} />
              </label>
            ))}
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding-top:8px;')}>
              <input aria-label="Reason for the change" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Reason for the change" style={sx("flex:1 1 160px;min-width:0;height:30px;box-sizing:border-box;padding:0 9px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FCFAF4;font-family:'Archivo',sans-serif;font-size:16px;")} />
              <a href="#" {...wr(() => void save())} style={sx(BTN30 + (why.trim() ? 'background:#264C7E;color:#fff;' : 'background:rgba(34,39,46,.07);color:#54606C;cursor:default;'))}>Save · passkey</a>
              <a href="#" onClick={(e) => { pre(e); setEditing(false); setDraft({}); setWhy(''); }} style={sx('font-size:16px;font-weight:600;color:#54606C;text-decoration:none;')}>Cancel</a>
            </div>
          </>
        )}
        {canEdit && !editing && (
          <div style={sx('padding-top:7px;' + ROWLINE)}>
            <a href="#" onClick={(e) => { pre(e); setEditing(true); }} style={sx('display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;border:1px solid rgba(47,92,151,.4);color:#2F5C97;')}>Edit policy</a>
          </div>
        )}
        <div style={sx(CAPT + 'margin:12px 0 2px;')}>Company limits on rules</div>
        {['No rule may allow email outside @aithoracare.com and deal contacts.', 'Every rule ends after 30 days unless renewed.', 'Anything from an email, page or file needs a fresh OK, even with a rule.'].map((x) => (
          <div key={x} style={sx('display:flex;gap:8px;padding:6px 0;font-size:16px;color:#22272E;line-height:1.4;' + ROWLINE)}>{x}</div>
        ))}
        {owner && (
          <div style={sx('display:flex;align-items:center;gap:10px;padding-top:8px;' + ROWLINE)}>
            <a href="#" {...wr(() => void revokeAll())} style={sx(BTN30 + 'border:1px solid rgba(179,38,30,.4);color:#B3261E;')}>Revoke all rules</a>
            <span style={sx('font-size:16px;color:#54606C;')}>{revoked ? 'All rules revoked at ' + hm(revoked) + '. Atlas asks before everything.' : 'Stops every team rule at once. Revoking never needs a passkey.'}</span>
          </div>
        )}
        <ErrLine error={err} />
      </div>
    </div>
  );
}

// ── Chrome copilot (DESIGN · NOT BUILT YET, AF-1582) ────────────────────────
// The design's interactive sample, ported as it is: a sample page in a browser frame and the
// side panel beside it. Nothing here reads a real page or does anything; it says so.

type Rev = 'hard' | 'undo' | 'care' | 'read';
const CP_TAG: Record<Rev, [string, string, string]> = {
  hard: ['HARD TO UNDO', '#7A4A0E', 'rgba(194,112,47,.16)'],
  undo: ['UNDOABLE', '#12503C', 'rgba(31,107,82,.13)'],
  care: ['CARE · A PERSON DECIDES', '#8A1C16', 'rgba(179,38,30,.11)'],
  read: ['READ ONLY', '#54606C', 'rgba(34,39,46,.08)'],
};
const cpTagSt = (t: Rev) => 'flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.05em;border-radius:5px;padding:2px 6px;white-space:nowrap;color:' + CP_TAG[t][1] + ';background:' + CP_TAG[t][2] + ';';
interface CpPage { site: string; label: string; url: string; kind: string; title: string; meta: string; body: string[]; next: string; why: string; acts: [string, string, string, Rev][] }
const CP: Record<'mail' | 'ehr' | 'cms', CpPage> = {
  mail: { site: 'mail.google.com', label: 'An email', url: 'mail.google.com · Inbox', kind: 'Email · from Belterra Health & Rehab', title: 'Re: Kickoff follow-up and next steps', meta: 'Dana Ruiz, Administrator · today 2:14 PM',
    body: ['Thanks for the call on the 30th. Our DON wants to see how the fall alerts route before we sign off on the pilot.', 'Could we do a 30-minute walkthrough next Tuesday or Wednesday afternoon? Also, can you send the BAA redline back by Friday?'],
    next: 'Reply with two walkthrough times and attach the BAA redline.', why: 'Dana asked two things; the deal is at Pilot agreement and the redline has been open 6 days.',
    acts: [['reply', 'Draft a reply', 'two times from your calendar + the redline', 'hard'], ['cal', 'Hold Tue 2:00 PM and Wed 3:00 PM', 'tentative holds on your calendar', 'undo'], ['crm', 'Log this to Belterra in the CRM', 'email and next step on the deal', 'undo'], ['task', 'Remind me Friday: BAA redline', 'a task on your To do', 'undo']] },
  ehr: { site: 'ehr.example', label: 'A resident chart', url: 'ehr.example · Resident chart', kind: 'EHR · C C Young', title: 'Beatrice Okoro · Rm 204', meta: 'Progress notes · last 14 days',
    body: ['Gait noted slower on evening rounds. One near-fall at bedside Sep 30, no injury.', 'Wearable shows gait speed down 18% over 14 days.'],
    next: 'Ask for a PT gait assessment for Beatrice Okoro.', why: 'Gait speed down 18% in 14 days plus a near-fall; this is a care call, so a clinician decides.',
    acts: [['pt', 'Propose a PT gait assessment', 'goes to the nurse on duty to sign', 'care'], ['note', 'Draft a progress note', 'from this chart page, for you to edit and sign', 'hard'], ['wl', 'Add a fall-risk check to the Worklist', 'tonight, Hall B', 'undo']] },
  cms: { site: 'cms.gov', label: 'A CMS page', url: 'cms.gov · Care Compare', kind: 'CMS Care Compare', title: 'The Arbour at Westminster Manor', meta: 'Overall 2 stars · health inspection 1 star',
    body: ['Most recent standard survey: 11 health deficiencies, 2 at harm level.', 'Staffing: 3.1 nurse hours per resident day.'],
    next: 'Pull Westminster Manor into Atlas Intelligence and compare it with its peers.', why: 'It is in your Texas territory, not yet in the CRM, and its scores fell this quarter.',
    acts: [['ai', 'Open it in Atlas Intelligence', 'facility record, peers and distress score', 'read'], ['prospect', 'Add as a prospect', 'Texas territory, Tier B', 'undo'], ['memo', 'Draft a one-page memo', 'scores, staffing and what we would fix', 'read']] },
};
const CP_DETAIL: Record<string, [string, string][]> = {
  reply: [['To', 'Dana Ruiz (dana.ruiz@belterra.example)'], ['Says', 'Tue 2:00 PM or Wed 3:00 PM for the walkthrough; BAA redline attached.'], ['Undo', 'Sends after 60 seconds; Undo until then.']],
  cal: [['Adds', 'Two tentative 30-minute holds'], ['Tells', 'Nobody until you send the reply']], crm: [['Adds', 'Email and next step on Belterra'], ['Tells', 'Nobody']], task: [['Adds', 'Friday 9:00 AM: send the BAA redline'], ['Tells', 'Only you']],
  pt: [['Creates', 'One PT gait assessment request on Beatrice’s care plan'], ['Goes to', 'The nurse on duty signs it; Atlas never orders care itself'], ['Tells', 'Not the family']], note: [['Drafts', 'A progress note from this page'], ['Then', 'You edit and sign it in the EHR']], wl: [['Adds', 'Fall-risk check tonight, Hall B'], ['Tells', 'The night shift']],
  ai: [['Opens', 'Westminster Manor in Atlas Intelligence']], prospect: [['Adds', 'Prospect, Texas territory, Tier B'], ['Tells', 'Nobody']], memo: [['Drafts', 'A one-page memo for you to read']],
};
const BTN_DARK = 'display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;background:#264C7E;color:#fff;';
const BTN_QUIET = 'display:inline-flex;align-items:center;height:30px;padding:0 10px;font-size:16px;font-weight:600;text-decoration:none;color:#54606C;';

export function AssistantCopilotTab() {
  const [pk, setPk] = useState<'mail' | 'ehr' | 'cms'>('mail');
  const [ok, setOk] = useState<Record<string, boolean>>({});
  const [no, setNo] = useState<Record<string, boolean>>({});
  const [use, setUse] = useState<Record<string, boolean>>({});
  const [sel, setSel] = useState<{ page: string; id: string; state: 'open' | 'pk' | 'done' | 'undone' | 'declined'; at?: number } | null>(null);
  const [, tick] = useState(0);
  const P = CP[pk];
  const allowed = !!ok[pk];
  const denied = !!no[pk];
  const using = allowed && !!use[pk];
  const s = sel && sel.page === pk ? sel : null;
  const act = s ? P.acts.find((a) => a[0] === s.id) ?? null : null;
  const left = s && s.at ? Math.max(0, 60 - Math.floor((Date.now() - s.at) / 1000)) : 0;
  const counting = !!s && s.state === 'done' && s.id === 'reply' && left > 0;
  useEffect(() => {
    if (!counting) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [counting]);
  const go = (fn: () => void) => (e: MouseEvent) => { pre(e); fn(); };
  const doneText = !s ? '' : s.state === 'declined' ? 'Declined. Nothing changed.' : s.state === 'undone' ? 'Undone. Nothing was sent.'
    : act && act[3] === 'care' ? 'Passkey checked. Sent to the nurse on duty to sign. Atlas did not order it.'
      : act && act[3] === 'read' ? 'Opened. Nothing was changed.'
        : s.id === 'reply' ? (left > 0 ? 'Sending in 0:' + String(left).padStart(2, '0') : 'Sent to Dana Ruiz. On the Truth Ledger.') : 'Done. On the Truth Ledger.';
  return (
    <div data-screen-label="Chrome copilot" style={sx('display:flex;flex-direction:column;gap:10px;min-width:0;')}>
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
        <AboutThis inline textStyle="font-size:16px;color:#3C454F;line-height:1.5;text-wrap:pretty;">
          Atlas in a Chrome side panel, next to whatever you have open: email, calendar, an EHR, a CMS page. You allow it per site, and it reads a page only when you press Use this page for that request. It suggests the next step, page text is treated as information and never as instructions, and every action uses the same approval card, Undo and Pause AI as the console.
        </AboutThis>
        <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#54606C;background:rgba(34,39,46,.08);white-space:nowrap;')}>DESIGN · NOT BUILT YET</span>
        <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 8px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;color:#264C7E;background:rgba(47,92,151,.12);white-space:nowrap;')}>AITHORA TEAM ONLY</span>
      </div>
      <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
        {(Object.keys(CP) as ('mail' | 'ehr' | 'cms')[]).map((k) => (
          <a key={k} href="#" onClick={go(() => setPk(k))} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (pk === k ? 'background:#264C7E;color:#fff;' : 'border:1px solid rgba(34,39,46,.16);color:#3C454F;'))}>{CP[k].label}</a>
        ))}
      </div>
      <div style={sx('border:1px solid rgba(34,39,46,.16);border-radius:12px;overflow:hidden;background:#E9E5DC;min-width:0;')}>
        <div style={sx('display:flex;align-items:center;gap:8px;height:34px;padding:0 12px;background:#DCD7CC;border-bottom:1px solid rgba(34,39,46,.12);')}>
          <span style={sx('width:9px;height:9px;border-radius:50%;background:#C5584F;')} />
          <span style={sx('width:9px;height:9px;border-radius:50%;background:#C9A14A;')} />
          <span style={sx('width:9px;height:9px;border-radius:50%;background:#5E9E6E;')} />
          <span style={sx('flex:1 1 auto;min-width:0;margin-left:10px;height:22px;border-radius:6px;background:#F6F3EC;display:flex;align-items:center;padding:0 10px;font-size:16px;color:#54606C;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{P.url}</span>
        </div>
        <div style={sx('display:grid;grid-template-columns:minmax(0,1fr) minmax(300px,360px);height:470px;')}>
          <div style={sx('padding:18px 20px;background:#FCFAF4;min-width:0;display:flex;flex-direction:column;gap:8px;')}>
            <span style={sx(CAPT)}>{P.kind}</span>
            <span style={sx('font-size:17px;font-weight:700;color:#22272E;line-height:1.3;')}>{P.title}</span>
            <span style={sx('font-size:16px;color:#54606C;')}>{P.meta}</span>
            {P.body.map((b) => <p key={b} style={sx('margin:0;font-size:16px;color:#22272E;line-height:1.55;text-wrap:pretty;')}>{b}</p>)}
            <span style={sx('margin-top:auto;font-size:16px;color:#54606C;')}>Sample page for the design. Not a real message.</span>
          </div>
          <div style={sx('background:#F6F3EC;border-left:1px solid rgba(34,39,46,.14);display:flex;flex-direction:column;min-width:0;min-height:0;')}>
            <div style={sx('display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('width:22px;height:22px;border-radius:6px;background:#264C7E;color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:800;')}>A</span>
              <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Atlas</span>
              <span style={sx('flex:1 1 10px;')} />
              <span style={sx('font-size:14px;font-weight:800;letter-spacing:.05em;border-radius:5px;padding:2px 7px;white-space:nowrap;' + (using ? 'color:#12503C;background:rgba(31,107,82,.13);' : 'color:#54606C;background:rgba(34,39,46,.08);'))}>{using ? 'Using this page' : allowed ? 'Allowed on site' : 'Not allowed'}</span>
              {using && <a href="#" onClick={go(() => { setUse({ ...use, [pk]: false }); setSel(s && s.state === 'done' ? s : null); })} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:7px;font-size:16px;font-weight:800;text-decoration:none;background:#B3261E;color:#fff;')}>Stop</a>}
            </div>
            <div style={sx('padding:10px 12px;display:grid;align-content:start;grid-auto-rows:max-content;gap:9px;flex:1 1 auto;min-height:0;overflow:auto;')}>
              {!allowed && !denied && (
                <div style={sx('display:flex;flex-direction:column;gap:7px;border:1px solid rgba(47,92,151,.3);border-radius:10px;padding:10px 12px;background:#FCFAF4;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{'Allow Atlas on ' + P.site + '?'}</span>
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>Even then it reads a page only when you press Use this page, for that one request. Never passwords, card numbers or other tabs. Change it any time in Chrome&apos;s site settings.</span>
                  <div style={sx('display:flex;gap:6px;')}>
                    <a href="#" onClick={go(() => setOk({ ...ok, [pk]: true }))} style={sx(BTN_DARK)}>Allow on this site</a>
                    <a href="#" onClick={go(() => setNo({ ...no, [pk]: true }))} style={sx(BTN_QUIET + 'padding:0 12px;')}>Not now</a>
                  </div>
                </div>
              )}
              {allowed && !using && (
                <div style={sx('display:flex;flex-direction:column;gap:7px;border:1px solid rgba(34,39,46,.14);border-radius:10px;padding:10px 12px;background:#FCFAF4;')}>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Atlas is not reading this page</span>
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>Press Use this page and Atlas reads it for this one request, then stops.</span>
                  <div style={sx('display:flex;')}>
                    <a href="#" onClick={go(() => setUse({ ...use, [pk]: true }))} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;background:#264C7E;color:#fff;')}>Use this page</a>
                  </div>
                </div>
              )}
              {using && (
                <>
                  {!act && (
                    <div style={sx('display:flex;flex-direction:column;gap:5px;border-radius:10px;padding:10px 12px;background:rgba(47,92,151,.08);')}>
                      <span style={sx(CAPT + 'color:#2F5C97;')}>Next for you</span>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.4;')}>{P.next}</span>
                      <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}><span style={sx('font-weight:700;')}>Because</span> {P.why}</span>
                    </div>
                  )}
                  <div style={sx('display:flex;align-items:baseline;gap:8px;')}>
                    <span style={sx(CAPT)}>{s ? 'Approve this' : 'Do from here'}</span>
                    <span style={sx('flex:1 1 10px;')} />
                    {act && <a href="#" onClick={go(() => setSel(null))} style={sx('font-size:16px;font-weight:700;color:#2F5C97;text-decoration:none;')}>Other actions</a>}
                  </div>
                  {!s && P.acts.map((a) => (
                    <a key={a[0]} href="#" onClick={go(() => setSel({ page: pk, id: a[0], state: 'open' }))} style={sx('display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:9px;text-decoration:none;min-height:44px;box-sizing:border-box;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
                      <span style={sx('flex:1 1 auto;min-width:0;')}>
                        <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{a[1]}</span>
                        <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:1px;')}>{a[2]}</span>
                      </span>
                      <span style={sx(cpTagSt(a[3]))}>{CP_TAG[a[3]][0]}</span>
                    </a>
                  ))}
                  {act && s && (
                    <div style={sx('border:1px solid rgba(34,39,46,.16);border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:6px;background:#FCFAF4;')}>
                      <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                        <span style={sx(cpTagSt(act[3]))}>{CP_TAG[act[3]][0]}</span>
                        {act[3] !== 'read' && <span style={sx('font-size:14px;font-weight:800;letter-spacing:.06em;border-radius:5px;padding:2px 7px;white-space:nowrap;color:#5B3A8C;background:rgba(122,106,174,.16);')}>FROM THIS PAGE · CHECK IT</span>}
                      </div>
                      <span style={sx('font-size:16px;font-weight:700;color:#22272E;line-height:1.4;')}>{act[1]}</span>
                      {(CP_DETAIL[act[0]] ?? []).map(([k, v]) => (
                        <div key={k} style={sx('display:grid;grid-template-columns:62px minmax(0,1fr);gap:8px;font-size:16px;line-height:1.45;')}>
                          <span style={sx('color:#54606C;font-weight:600;')}>{k}</span>
                          <span style={sx('color:#22272E;')}>{v}</span>
                        </div>
                      ))}
                      {s.state === 'pk' && (
                        <div style={sx('display:flex;flex-direction:column;gap:6px;border-radius:9px;padding:9px 10px;background:rgba(47,92,151,.08);')}>
                          <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Confirm it&apos;s you</span>
                          <span style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>Care requests need your passkey (Touch ID or your security key), so the server knows a person approved it.</span>
                          <div style={sx('display:flex;gap:6px;')}>
                            <a href="#" onClick={go(() => setSel({ ...s, state: 'done', at: Date.now() }))} style={sx(BTN_DARK)}>Use passkey</a>
                            <a href="#" onClick={go(() => setSel({ ...s, state: 'open' }))} style={sx(BTN_QUIET)}>Cancel</a>
                          </div>
                        </div>
                      )}
                      {s.state === 'open' && (
                        <div style={sx('display:flex;gap:6px;flex-wrap:wrap;')}>
                          <a href="#" onClick={go(() => setSel(act[3] === 'care' ? { ...s, state: 'pk' } : { ...s, state: 'done', at: Date.now() }))} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;background:#1F6B52;color:#fff;')}>{act[3] === 'care' ? 'Approve with passkey' : 'Approve'}</a>
                          <a href="#" onClick={go(() => setSel({ ...s, state: 'declined' }))} style={sx('display:inline-flex;align-items:center;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;border:1px solid rgba(179,38,30,.4);color:#B3261E;')}>Decline</a>
                        </div>
                      )}
                      {s.state !== 'open' && s.state !== 'pk' && (
                        <div style={sx('display:flex;align-items:center;gap:8px;font-size:16px;color:#12503C;font-weight:600;')}>
                          <span style={sx('flex:1 1 auto;')}>{doneText}</span>
                          {counting && <a href="#" onClick={go(() => setSel({ ...s, state: 'undone' }))} style={sx('font-size:16px;font-weight:800;color:#2F5C97;text-decoration:none;')}>Undo</a>}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {denied && !allowed && <div style={sx('font-size:16px;color:#54606C;line-height:1.45;')}>Atlas isn&apos;t allowed on this site. Ask it anything below; it answers from the console, not this page.</div>}
            </div>
            <div style={sx('display:flex;align-items:center;gap:8px;padding:8px 12px;border-top:1px solid rgba(34,39,46,.1);')}>
              <span style={sx('flex:1 1 auto;height:32px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FCFAF4;display:flex;align-items:center;padding:0 10px;font-size:16px;color:#6B7480;')}>Ask Atlas about this page…</span>
              <span style={sx('font-size:16px;font-weight:700;color:#B3261E;white-space:nowrap;')}>Pause AI</span>
            </div>
          </div>
        </div>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>Rules: allowed per site · reads a page only after Use this page, for that request · Stop always visible while it works · page text is information, never instructions · never passwords, card numbers or other tabs · care approvals need a passkey · anything taken from a page needs your fresh OK and no rule can cover it · clinical actions always go to a person · every action is on the Truth Ledger. Back end: AF-1582.</div>
    </div>
  );
}


